import {RouterLinkTarget} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React from 'react';
import {FONT_WEIGHTS} from '../../../constants/typography';
import {ImageType} from '../../../types/Image';
import {Image} from '../Image/Image';
import {Breakpoints} from '../Image/types';
import {Tooltip} from '../Tooltip/Tooltip';
import {UnstyledLink} from '../UnstyledLink/UnstyledLink';

export interface BrandLogoProps {
  path?: RouterLinkTarget | string;
  name: string;
  onClick?: (e: React.MouseEvent) => void;
  openExternalLinkInNewTab?: boolean;
  image?: ImageType;
  className?: string;
  style?: React.CSSProperties;
  /** max height including unit */
  maxHeight?: string;
  /** max width including unit */
  maxWidth?: string;
  breakpoints?: Breakpoints;
  imageWidths?: Array<number>;
  /** Alignment as defined in css `object-position`, e.g. left */
  alignment?: string;
  showTooltip?: boolean;
  shouldShowBrandAsText?: boolean;
}

export function BrandLogo({
  image,
  name,
  path = '',
  maxWidth = 'auto',
  maxHeight = 'auto',
  imageWidths = DEFAULT_WIDTHS,
  breakpoints,
  alignment = 'center',
  showTooltip = true,
  ...rest
}: BrandLogoProps) {
  return (
    <ConditionalLinkWrapper
      data-testid="brandLogoContainer"
      path={path}
      {...rest}
    >
      {image?.url ? (
        <ConditionalTooltip showTooltip={showTooltip} label={name}>
          <StyledImage
            data-testid="brandLogo"
            src={image.url}
            alt={image.description}
            disableFixedRatio
            ratio="1:1"
            breakpoints={breakpoints ?? []}
            widths={imageWidths}
            $maxHeight={maxHeight}
            $maxWidth={maxWidth}
            $alignment={alignment}
          />
        </ConditionalTooltip>
      ) : (
        <StyledBrandName data-testid="brandName">{name}</StyledBrandName>
      )}
    </ConditionalLinkWrapper>
  );
}

const DEFAULT_WIDTHS = [250, 350, 450, 550];

const ConditionalLinkWrapper: React.FC<
  React.PropsWithChildren<Partial<BrandLogoProps> & {['data-testid']?: string}>
> = ({children, path, 'data-testid': dataTestId, ...rest}) => {
  if (path) {
    return (
      <StyledLink
        data-testid={dataTestId}
        to={path}
        aria-label={undefined}
        {...rest}
      >
        {children}
      </StyledLink>
    );
  }

  return (
    <BrandWrapper data-testid={dataTestId} {...rest}>
      {children}
    </BrandWrapper>
  );
};

const ConditionalTooltip: React.FC<
  React.PropsWithChildren<{showTooltip?: boolean; label: string}>
> = ({children, showTooltip, label}) => {
  if (showTooltip) {
    return <Tooltip label={label}>{children}</Tooltip>;
  }

  return <>{children}</>;
};

const StyledBrandName = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const BrandWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const StyledLink = styled(UnstyledLink)`
  font-weight: ${FONT_WEIGHTS.bold};
  width: 100%;
  height: 100%;
`;

const StyledImage = styled(Image)<{
  $alignment: string;
  $maxHeight: string;
  $maxWidth: string;
  $maxWidthTabletAndUp?: string;
}>`
  object-fit: contain;
  object-position: ${({$alignment}) => $alignment};
  /* height and width are considered max values due to aspect ratio + object-fit */
  width: ${({$maxWidth}) => $maxWidth};
  height: ${({$maxHeight}) => $maxHeight};
  max-width: 100%;
`;
